import {
    listAllStns,
    listAllStnsBySku,
    listStns,
    listStnsBySku,
    listStnSources,
    listWarehousesForStn,
    type Stn,
    type StnBySkuRow,
    type StnStatus,
} from '@api/warehouse-ops';
import PageHeader from '@components/common/PageHeader';
import { ColumnPicker } from '@components/ui/ColumnPicker';
import { DateRangePicker, type UiDateRange } from '@components/ui/DateRange';
import {
    DataSheet,
    type DataSheetFilterConfig,
    DataSheetPagination,
    type DataSheetTab,
} from '@components/ui/DataSheet';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import usePersistentState from '@hooks/usePersistentState';
import { Button, Group, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { Plus } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router';

import CreateStnDrawer from './CreateStnDrawer';
import {
    buildSkuColumns,
    buildStnColumns,
    buildStnNextStepColumn,
    COLUMN_DEFS,
    DEFAULT_HIDDEN_COLUMNS,
    OPTIONAL_COLUMN_DEFS,
} from './stnColumns';
import StnDetailPanel from './StnDetailPanel';
import { SOURCE_LABEL, STATUS_LABEL } from './stnLabels';
import StnSkuPanel from './StnSkuPanel';
import { useStnReceivePath } from './stnReceivePath';
import { stnNextStep, useStnActions, type StnNextStep } from './useStnActions';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * The two sheets on the grid. Notes is the record: every note under the filters and the
 * date range. By SKU is the question "what is still coming for each SKU": open notes only,
 * no date range, so a note raised months ago and still open is counted.
 */
type Sheet = 'notes' | 'sku';
const SHEET_TABS: DataSheetTab[] = [
    { id: 'notes', label: 'Notes' },
    { id: 'sku', label: 'By SKU' },
];

const EMPTY_FILTERS: FilterValue = { source: [], status: [], from: [], to: [] };

/** Last 30 days, by the date stock moved — the page opens on recent transfers. */
const defaultRange = (): UiDateRange => ({
    startDate: format(subDays(new Date(), 29), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
});

const StockTransferNotesContainer: FC = () => {
    const [sheet, setSheet] = usePersistentState<Sheet>('stn_sheet_v1', 'notes');
    const isSku = sheet === 'sku';
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 300);
    const [filters, setFilters] = useState<FilterValue>(EMPTY_FILTERS);
    const [range, setRange] = usePersistentState<UiDateRange>('stn_date_range_v1', defaultRange());
    const [hiddenColumns, setHiddenColumns] = usePersistentState<string[]>(
        'stn_hidden_cols_v1',
        DEFAULT_HIDDEN_COLUMNS,
    );
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedSku, setSelectedSku] = useState<StnBySkuRow | null>(null);
    const navigate = useNavigate();
    const { approve, dispatch } = useStnActions();
    // A transfer's receipt opens where its stock lands: a store's RM/PM GRN, a site, or the FG form.
    const { pathFor: receivePathFor, ready: receiveReady } = useStnReceivePath();

    const warehousesQuery = useQuery({
        queryKey: ['stn-warehouses'],
        queryFn: listWarehousesForStn,
        staleTime: 5 * 60_000,
    });
    const sourcesQuery = useQuery({
        queryKey: ['stn-sources'],
        queryFn: listStnSources,
        staleTime: 5 * 60_000,
    });

    // The narrowing both sheets share; the SKU panel takes the same so it lists what its row counted.
    const sharedParams = useMemo(
        () => ({
            sources: filters.source,
            fromNodeIds: filters.from,
            toNodeIds: filters.to,
            search: debouncedSearch || undefined,
        }),
        [filters.source, filters.from, filters.to, debouncedSearch],
    );

    const notesQuery = useQuery({
        queryKey: ['stns', page, pageSize, filters, debouncedSearch, range],
        queryFn: () =>
            listStns({
                page,
                take: pageSize,
                // Whole local days, sent as instants, matched against the date stock moved.
                dateFrom: startOfDay(parseISO(range.startDate)).toISOString(),
                dateTo: endOfDay(parseISO(range.endDate)).toISOString(),
                statuses: filters.status as StnStatus[],
                ...sharedParams,
            }),
        keepPreviousData: true,
        enabled: !isSku,
    });
    const skuQuery = useQuery({
        queryKey: ['stns-by-sku', page, pageSize, sharedParams],
        queryFn: () => listStnsBySku({ page, take: pageSize, ...sharedParams }),
        keepPreviousData: true,
        enabled: isSku,
    });

    const filterConfig = useMemo<DataSheetFilterConfig>(() => {
        const warehouseOptions = (warehousesQuery.data ?? [])
            .map((warehouse) => ({ label: warehouse.name, value: warehouse.warehouseId }))
            .sort((a, b) => a.label.localeCompare(b.label));
        const params: DataSheetFilterConfig['params'] = [
            {
                key: 'from',
                label: 'From',
                mode: 'multi',
                options: warehouseOptions,
                searchPlaceholder: 'Search warehouses...',
            },
            {
                key: 'to',
                label: 'To',
                mode: 'multi',
                options: warehouseOptions,
                searchPlaceholder: 'Search warehouses...',
            },
        ];
        // Status is fixed to open on the By SKU sheet, so the choice is offered only on Notes.
        if (!isSku) {
            params.unshift({
                key: 'status',
                label: 'Status',
                mode: 'multi',
                options: (Object.keys(STATUS_LABEL) as StnStatus[]).map((value) => ({
                    value,
                    label: STATUS_LABEL[value],
                })),
            });
        }
        // Only the sources that have written a note for this brand. One source is no choice,
        // so the param is left out and the menu opens on the next one.
        const sources = sourcesQuery.data ?? [];
        if (sources.length > 1) {
            params.unshift({
                key: 'source',
                label: 'Source',
                mode: 'multi',
                options: sources.map((value) => ({ value, label: SOURCE_LABEL[value] ?? value })),
            });
        }
        return {
            label: 'Transfer',
            params,
            value: filters,
            onChange: (value) => {
                setFilters({ ...EMPTY_FILTERS, ...value });
                setPage(1);
            },
        };
    }, [warehousesQuery.data, sourcesQuery.data, filters, isSku]);

    // The full set stays as `allColumns` so a download carries what the columns menu hides.
    const allNoteColumns = useMemo(() => buildStnColumns(), []);

    const { mutate: approveStn } = approve;
    const { mutate: dispatchStn } = dispatch;
    const runStep = useCallback(
        (step: StnNextStep, row: Stn) => {
            if (step === 'APPROVE') approveStn(row.id);
            else if (step === 'DISPATCH') dispatchStn(row.id);
            else {
                // Received on its page: the CM site's Receive tab, the store's or warehouse's GRN.
                const path = receiveReady ? receivePathFor(row) : null;
                if (path) void navigate(path);
            }
        },
        [approveStn, dispatchStn, receiveReady, receivePathFor, navigate],
    );
    // Receive only where the STN has a page it is received on; until the stores are known, none.
    const nextStepFor = useCallback(
        (row: Stn) => stnNextStep(row, receiveReady && !!receivePathFor(row)),
        [receiveReady, receivePathFor],
    );
    // The step in flight and its row, so only that row's button spins.
    const pendingStep: StnNextStep | null = approve.isPending ? 'APPROVE' : dispatch.isPending ? 'DISPATCH' : null;
    const pendingStnId = approve.isPending ? approve.variables : dispatch.isPending ? dispatch.variables : undefined;
    // On screen only: the next-step column sits right after Status. It stays out of
    // allNoteColumns, so the download carries the data columns alone.
    const noteColumns = useMemo(() => {
        const visible = allNoteColumns.filter((column) => !hiddenColumns.includes(column.key));
        const nextStep = buildStnNextStepColumn({
            nextStepFor,
            onStep: runStep,
            pending: pendingStep && pendingStnId ? { step: pendingStep, stnId: pendingStnId } : null,
        });
        const at = visible.findIndex((column) => column.key === 'status');
        return at === -1 ? [...visible, nextStep] : [...visible.slice(0, at + 1), nextStep, ...visible.slice(at + 1)];
    }, [allNoteColumns, hiddenColumns, nextStepFor, runStep, pendingStep, pendingStnId]);
    const skuColumns = useMemo(() => buildSkuColumns(), []);

    const activeQuery = isSku ? skuQuery : notesQuery;
    const meta = activeQuery.data?.meta;
    const total = meta?.itemCount ?? 0;
    const hasFilters =
        !!debouncedSearch ||
        Object.entries(filters).some(([key, values]) => values.length > 0 && !(isSku && key === 'status'));

    const switchSheet = (next: string) => {
        setSheet(next as Sheet);
        setPage(1);
        // A status picked on Notes has no meaning on By SKU; clear it rather than carry it silently.
        if (next === 'sku' && filters.status.length > 0) setFilters({ ...filters, status: [] });
    };

    const subtitle = isSku
        ? `${total.toLocaleString('en-IN')} ${hasFilters ? 'matching ' : ''}SKU${total === 1 ? '' : 's'} with units still to land · no date range`
        : `${total.toLocaleString('en-IN')} ${hasFilters ? 'matching ' : ''}transfer${total === 1 ? '' : 's'}`;

    // Everything the two sheets share on the grid; only data, columns and row handling differ.
    const sheetProps = {
        title: 'Stock Transfer Notes',
        subtitle,
        enableDownload: true,
        tabs: SHEET_TABS,
        activeTab: sheet,
        onTabChange: switchSheet,
        isLoading: activeQuery.isLoading,
        enableSearch: true,
        searchPlaceholder: 'Search by STN or gate-pass code...',
        searchValue: search,
        onSearchChange: (value: string) => {
            setSearch(value);
            setPage(1);
        },
        filters: [filterConfig],
        toolbarControls: (
            <Group gap="xs" wrap="nowrap">
                {!isSku && (
                    <DateRangePicker
                        value={range}
                        onChange={(next) => {
                            setRange(next);
                            setPage(1);
                        }}
                        title="Transfers dispatched in this range"
                    />
                )}
                <Button size="xs" leftSection={<Plus size={12} />} onClick={() => setDrawerOpen(true)}>
                    Create STN
                </Button>
            </Group>
        ),
        toolbarActionsRight: isSku ? undefined : (
            <ColumnPicker
                defaultOnDefs={COLUMN_DEFS}
                defaultOffDefs={OPTIONAL_COLUMN_DEFS}
                hiddenKeys={hiddenColumns}
                onChange={setHiddenColumns}
            />
        ),
        emptyState: (
            <Text size="13px" c="dimmed" ta="center" p={32}>
                {isSku
                    ? hasFilters
                        ? 'Nothing still to land matches these filters.'
                        : 'Nothing still to land.'
                    : hasFilters
                      ? 'No transfers match these filters.'
                      : 'No stock transfers yet.'}
            </Text>
        ),
        footer: meta ? (
            <DataSheetPagination
                currentPage={meta.page}
                totalPages={Math.max(1, meta.pageCount)}
                pageSize={pageSize}
                totalItems={total}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
            />
        ) : undefined,
    };

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🔁"
                title="Stock Transfers"
                subtitle="Stock moving between your warehouses, channels and vendors: gate passes, FBA shipments and STNs raised here"
            />
            {isSku ? (
                <DataSheet<StnBySkuRow>
                    {...sheetProps}
                    data={skuQuery.data?.data ?? []}
                    columns={skuColumns}
                    getRowKey={(row) => `${row.itemType}:${row.itemId}`}
                    onRowClick={(row) => setSelectedSku(row)}
                    enableColumnPicker
                    columnPickerKey="stn_sku_sheet_cols_v1"
                    downloadFilename="stock-transfers-by-sku.csv"
                    onFetchAllData={async () => ({ rows: await listAllStnsBySku(sharedParams) })}
                />
            ) : (
                <DataSheet<Stn>
                    {...sheetProps}
                    data={notesQuery.data?.data ?? []}
                    columns={noteColumns}
                    allColumns={allNoteColumns}
                    getRowKey={(row) => row.id}
                    onRowClick={(row) => setSelectedId(row.id)}
                    downloadFilename="stock-transfers.csv"
                    onFetchAllData={async () => ({
                        rows: await listAllStns({
                            dateFrom: startOfDay(parseISO(range.startDate)).toISOString(),
                            dateTo: endOfDay(parseISO(range.endDate)).toISOString(),
                            statuses: filters.status as StnStatus[],
                            ...sharedParams,
                        }),
                    })}
                />
            )}
            <CreateStnDrawer opened={drawerOpen} onClose={() => setDrawerOpen(false)} />
            {selectedSku && (
                <StnSkuPanel
                    itemId={selectedSku.itemId}
                    itemName={selectedSku.itemName}
                    filters={sharedParams}
                    onClose={() => setSelectedSku(null)}
                    onOpenNote={(stnId) => {
                        setSelectedSku(null);
                        setSelectedId(stnId);
                    }}
                />
            )}
            {selectedId && <StnDetailPanel stnId={selectedId} onClose={() => setSelectedId(null)} />}
        </div>
    );
};

export default StockTransferNotesContainer;
